import { Routes, Route, Navigate, useParams } from 'react-router-dom'
import { Toaster } from '@/components/ui/toaster'
import { ConfirmDialogProvider } from '@/components/confirm-dialog'
import { ProtectedRoute } from '@/components/layout/protected-route'
import DashboardLayout from '@/components/layout/dashboard-layout'
import LoginPage from '@/features/auth/login'
import { ModuleErrorBoundary } from '@/components/error-boundary'

// 懒加载各功能模块（按需加载，减少首屏体积）
import { lazy, Suspense, type ComponentType } from 'react'

/**
 * 带错误处理的 lazy loader
 * 部署后旧 chunk hash 失效时，自动硬刷新页面获取最新资源（防止白屏）。
 * 使用 sessionStorage 防止无限刷新循环（同一会话内最多自动刷新 1 次）。
 */
const CHUNK_RELOAD_KEY = 'quant_chunk_reload_ts'

function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
): ComponentType<any> {
  return lazy(() =>
    importFn().catch((err) => {
      const isChunkError =
        err?.name === 'ChunkLoadError' ||
        /Loading chunk|Loading CSS|Failed to fetch dynamically imported module|module/i.test(
          err?.message || '',
        )

      if (isChunkError) {
        const lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || '0')
        const now = Date.now()
        // 60 秒内不重复刷新，防止死循环
        if (now - lastReload > 60_000) {
          sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now))
          window.location.reload()
          // 返回一个永不 resolve 的 Promise，避免渲染闪烁
          return new Promise(() => {})
        }
      }

      throw new Error(`模块加载失败：${err.message || '未知错误'}`)
    }),
  )
}

const DataCenterModule = lazyWithRetry(() => import('@/features/trading/data-center').then(m => ({ default: m.DataCenterModule })))
const DataSourceHealthModule = lazyWithRetry(() => import('@/features/data-center/datasource-health').then(m => ({ default: m.DataSourceHealthModule })))
const MonitorPageModule = lazyWithRetry(() => import('@/features/scene/monitor-page').then(m => ({ default: m.MonitorPage })))
const QuotesModule = lazyWithRetry(() => import('@/features/trading/quotes').then(m => ({ default: m.QuotesModule })))
const ScreenerModule = lazyWithRetry(() => import('@/features/trading/screener').then(m => ({ default: m.ScreenerModule })))
const StrategyDevModule = lazyWithRetry(() => import('@/features/trading/strategy').then(m => ({ default: m.StrategyDevModule })))
const BacktestModule = lazyWithRetry(() => import('@/features/trading/backtest').then(m => ({ default: m.BacktestModule })))
const OMSModule = lazyWithRetry(() => import('@/features/trading/oms').then(m => ({ default: m.OMSModule })))
const RiskModule = lazyWithRetry(() => import('@/features/trading/risk').then(m => ({ default: m.RiskModule })))
const ResearchTeamPageModule = lazyWithRetry(() => import('@/features/trading/research-team-page').then(m => ({ default: m.ResearchTeamPage })))
const ResearchWorkspaceModule = lazyWithRetry(() => import('@/features/research/workspace-page').then(m => ({ default: m.ResearchWorkspacePage })))
const ApmModule = lazyWithRetry(() => import('@/features/system/performance-panel').then(m => ({ default: m.PerformancePanel })))
const AlertCenterModule = lazyWithRetry(() => import('@/features/trading/alert-center').then(m => ({ default: m.AlertCenterModule })))
const CalendarsModule = lazyWithRetry(() => import('@/features/calendars/module').then(m => ({ default: m.CalendarsModule })))
const FinancialsWorkbenchModule = lazyWithRetry(() => import('@/features/financials/financials-workbench').then(m => ({ default: m.FinancialsWorkbench })))
const PaperModule = lazyWithRetry(() => import('@/features/paper/module').then(m => ({ default: m.PaperModule })))
const SettingsPage = lazyWithRetry(() => import('@/features/settings/settings'))
const BriefingSharePage = lazyWithRetry(() =>
  import('@/features/briefing/briefing-share-page').then((m) => ({ default: m.BriefingSharePage })),
)

// /market/:ticker 跳转组件：将 URL 中的 ticker 存入 sessionStorage，然后重定向到 /quotes
function MarketTickerRedirect() {
  const { ticker } = useParams<{ ticker: string }>()
  // 同步设置 sessionStorage（在 render 阶段，确保 <Navigate> 触发前已写入）
  if (ticker) {
    sessionStorage.setItem('quant_target_symbol', decodeURIComponent(ticker))
  }
  return <Navigate to="/quotes" replace />
}

function LoadingFallback() {
  return (
    <div className="flex items-center justify-center h-full w-full bg-background/50 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <p className="text-xs text-muted-foreground font-mono">Loading Module...</p>
      </div>
    </div>
  )
}

export default function App() {
  return (
    <>
      <ConfirmDialogProvider>
        <Routes>
          {/* 登录页（无布局） */}
          <Route path="/login" element={<LoginPage />} />

          {/* 主应用布局（带认证保护） */}
          <Route element={<ProtectedRoute />}>
            <Route element={<DashboardLayout />}>
            <Route index element={<Navigate to="/data-center" replace />} />
            <Route path="/market/:ticker" element={<MarketTickerRedirect />} />
            <Route path="/data-center" element={<Suspense fallback={<LoadingFallback />}><ModuleErrorBoundary name="DataCenter"><DataCenterModule /></ModuleErrorBoundary></Suspense>} />
            <Route path="/macro" element={<Navigate to="/data-center" replace />} />
            <Route path="/fund-flow-dashboard" element={<Navigate to="/data-center" replace />} />
            <Route path="/quotes" element={<Suspense fallback={<LoadingFallback />}><ModuleErrorBoundary name="Quotes"><QuotesModule /></ModuleErrorBoundary></Suspense>} />
            <Route path="/monitor" element={<Suspense fallback={<LoadingFallback />}><MonitorPageModule /></Suspense>} />
            <Route path="/options" element={<Navigate to="/quotes" replace />} />
            <Route path="/screener" element={<Suspense fallback={<LoadingFallback />}><ModuleErrorBoundary name="Screener"><ScreenerModule /></ModuleErrorBoundary></Suspense>} />
            <Route path="/strategy" element={<Suspense fallback={<LoadingFallback />}><ModuleErrorBoundary name="Strategy"><StrategyDevModule /></ModuleErrorBoundary></Suspense>} />
            <Route path="/backtest" element={<Suspense fallback={<LoadingFallback />}><ModuleErrorBoundary name="Backtest"><BacktestModule /></ModuleErrorBoundary></Suspense>} />
            <Route path="/oms" element={<Suspense fallback={<LoadingFallback />}><ModuleErrorBoundary name="OMS"><OMSModule /></ModuleErrorBoundary></Suspense>} />
            <Route path="/risk" element={<Suspense fallback={<LoadingFallback />}><ModuleErrorBoundary name="Risk"><RiskModule /></ModuleErrorBoundary></Suspense>} />
            {/* UI 拆分：AI Copilot 已迁至左侧「投研」分栏，旧 /copilot 深链重定向 */}
            <Route path="/copilot" element={<Navigate to="/research" replace />} />
            <Route path="/research" element={<Suspense fallback={<LoadingFallback />}><ModuleErrorBoundary name="Research"><ResearchWorkspaceModule /></ModuleErrorBoundary></Suspense>} />
            <Route path="/research-team" element={<Suspense fallback={<LoadingFallback />}><ModuleErrorBoundary name="ResearchTeam"><ResearchTeamPageModule /></ModuleErrorBoundary></Suspense>} />
            <Route path="/apm" element={<Suspense fallback={<LoadingFallback />}><ModuleErrorBoundary name="APM"><ApmModule /></ModuleErrorBoundary></Suspense>} />
            <Route path="/alerts" element={<Suspense fallback={<LoadingFallback />}><ModuleErrorBoundary name="Alerts"><AlertCenterModule /></ModuleErrorBoundary></Suspense>} />
            <Route path="/calendars" element={<Suspense fallback={<LoadingFallback />}><ModuleErrorBoundary name="Calendars"><CalendarsModule /></ModuleErrorBoundary></Suspense>} />
            <Route path="/financials" element={<Suspense fallback={<LoadingFallback />}><ModuleErrorBoundary name="Financials"><FinancialsWorkbenchModule /></ModuleErrorBoundary></Suspense>} />
            <Route path="/datasource-health" element={<Suspense fallback={<LoadingFallback />}><ModuleErrorBoundary name="DataSourceHealth"><DataSourceHealthModule /></ModuleErrorBoundary></Suspense>} />
            <Route path="/paper" element={<Suspense fallback={<LoadingFallback />}><ModuleErrorBoundary name="Paper"><PaperModule /></ModuleErrorBoundary></Suspense>} />
            <Route path="/settings" element={<Suspense fallback={<LoadingFallback />}><ModuleErrorBoundary name="Settings"><SettingsPage /></ModuleErrorBoundary></Suspense>} />
            <Route path="/briefing/:id" element={<Suspense fallback={<LoadingFallback />}><ModuleErrorBoundary name="Briefing"><BriefingSharePage /></ModuleErrorBoundary></Suspense>} />
            </Route>
          </Route>
        </Routes>

        {/* 全局 Toast 通知 */}
        <Toaster />
      </ConfirmDialogProvider>
    </>
  )
}
